/**
 * "When was this zone last cleaned", as one colour: the operator views'
 * cleaned-zone colouring (the Overview's Building view and the 2D map).
 *
 * THE RULE (Akash, 2026-10-22). Green is a clean that has just finished. It
 * fades evenly to grey at the person's threshold (Preferences, 1-14 days,
 * default 3). A zone with no successful clean inside the threshold -- or
 * none ever -- is fully grey. A zone being cleaned right now is the live
 * accent, and pulses on the 3D view.
 *
 * "Cleaned" is ANY completed clean, of any type: vacuum, sweep, wet scrub
 * (all `category: clean`, the type in `params.mode`), and the older cleans
 * that name no type. That is exactly the server's `lastOkMs`
 * (`api/zones.py`: `category == "clean" AND state == "completed"`, no mode
 * filter). A failed or cancelled clean never sets it.
 *
 * DELIBERATELY SIMPLER THAN `/twin`. The demo screen keeps its evidence
 * states (`freshness.ts`: hatched when the trail cannot confirm a clean,
 * red when it contradicts one). Here a completed clean is a completed clean:
 * the views are for "where has the robot been lately", read at a glance by
 * hotel staff, and three extra encodings would need a legend nobody reads.
 *
 * Pure, three-free and DOM-free except `readCleanedRamp`, so both views and
 * the tests share it.
 */
import type { ZoneFreshness } from '../../api/twin';

/** Preferences bounds and default for the threshold, in whole days. */
export const CLEANED_DAYS_MIN = 1;
export const CLEANED_DAYS_MAX = 14;
export const CLEANED_DAYS_DEFAULT = 3;

const MS_PER_DAY = 86_400_000;

/** A stored or typed threshold, made safe: an integer within the bounds. */
export function clampCleanedDays(value: unknown): number {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (!Number.isFinite(n)) return CLEANED_DAYS_DEFAULT;
  return Math.max(CLEANED_DAYS_MIN, Math.min(CLEANED_DAYS_MAX, Math.round(n)));
}

export type CleanedTint =
  /** Cleaned inside the threshold; `t` 0 (just now) to <1 (almost due). */
  | { readonly kind: 'cleaned'; readonly t: number }
  | { readonly kind: 'in-progress' }
  /** Not cleaned inside the threshold, or never: fully grey. */
  | { readonly kind: 'stale' };

/**
 * How one zone is coloured. `zone` undefined is a cleaning zone the server
 * said nothing about -- never cleaned, as far as anyone can show -- so grey.
 */
export function cleanedTint(
  zone: Pick<ZoneFreshness, 'status' | 'lastOkMs'> | undefined,
  nowMs: number,
  thresholdDays: number,
): CleanedTint {
  if (zone?.status === 'in_progress') return { kind: 'in-progress' };
  if (zone?.lastOkMs === null || zone?.lastOkMs === undefined) return { kind: 'stale' };
  const horizon = Math.max(1, thresholdDays) * MS_PER_DAY;
  const age = Math.max(0, nowMs - zone.lastOkMs);
  if (age >= horizon) return { kind: 'stale' };
  return { kind: 'cleaned', t: age / horizon };
}

/** The ramp's two ends and the live accent, as `#rrggbb`. */
export interface CleanedRamp {
  readonly fresh: string;
  readonly stale: string;
  readonly live: string;
}

/** Dark-theme values, for when the tokens cannot be read (tests, SSR). The
 *  source of truth is `tokens.css`. */
export const CLEANED_RAMP_FALLBACK: CleanedRamp = {
  fresh: '#3fbf6b',
  stale: '#5d6874',
  live: '#ffb547',
};

/** The ramp for the CURRENT theme, read off the document's tokens. Read at
 *  paint time; each host repaints on a theme flip (`useThemeEpoch`). */
export function readCleanedRamp(): CleanedRamp {
  if (typeof document === 'undefined') return CLEANED_RAMP_FALLBACK;
  const style = getComputedStyle(document.documentElement);
  const css = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  return {
    fresh: css('--nx-cleaned-fresh', CLEANED_RAMP_FALLBACK.fresh),
    stale: css('--nx-cleaned-stale', CLEANED_RAMP_FALLBACK.stale),
    live: css('--nx-moving', CLEANED_RAMP_FALLBACK.live),
  };
}

// ---- perceptually even mixing (OKLab) -----------------------------------
//
// A straight sRGB mix from a saturated green to a grey goes muddy and dark in
// the middle, so "half-way to due" would read as dirtier than "due". OKLab is
// built so equal steps look equal, which is what "fades evenly" asks for.

function toLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}
function fromLinear(v: number): number {
  const c = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.round(Math.max(0, Math.min(1, c)) * 255);
}
function parseHex(hex: string): [number, number, number] {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [128, 128, 128];
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToOklab([r, g, b]: [number, number, number]): [number, number, number] {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}
function oklabToRgb([L, a, b]: [number, number, number]): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    fromLinear(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    fromLinear(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    fromLinear(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ];
}

/** `a` to `b` at `t` in OKLab, as `[r, g, b]`. The ends are exact. */
export function mixOklab(a: string, b: string, t: number): [number, number, number] {
  const k = Math.max(0, Math.min(1, t));
  if (k === 0) return parseHex(a);
  if (k === 1) return parseHex(b);
  const pa = rgbToOklab(parseHex(a));
  const pb = rgbToOklab(parseHex(b));
  return oklabToRgb([
    pa[0] + (pb[0] - pa[0]) * k,
    pa[1] + (pb[1] - pa[1]) * k,
    pa[2] + (pb[2] - pa[2]) * k,
  ]);
}

/** A tint's colour, as `[r, g, b]`. */
export function cleanedRgb(tint: CleanedTint, ramp: CleanedRamp): [number, number, number] {
  switch (tint.kind) {
    case 'cleaned':
      return mixOklab(ramp.fresh, ramp.stale, tint.t);
    case 'in-progress':
      return parseHex(ramp.live);
    case 'stale':
      return parseHex(ramp.stale);
  }
}

/** `[r, g, b]` as `#rrggbb`. */
export function rgbHex([r, g, b]: readonly [number, number, number]): string {
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The server's zones with the ones being cleaned right now marked so.
 *
 * The freshness endpoint never says `in_progress` (it reads finished rows
 * only); the live task list does. Zones the server did not list (a degraded
 * registry, a zone never dispatched before) are added, so a clean running in
 * one still shows. Identity is kept when nothing is active.
 */
export function withInProgress(
  zones: readonly ZoneFreshness[],
  activeZones: ReadonlySet<string>,
): readonly ZoneFreshness[] {
  if (activeZones.size === 0) return zones;
  const seen = new Set<string>();
  const out: ZoneFreshness[] = zones.map((z) => {
    seen.add(z.zone);
    return activeZones.has(z.zone) ? { ...z, status: 'in_progress' } : z;
  });
  for (const zone of activeZones) {
    if (seen.has(zone)) continue;
    out.push({
      zone,
      level: null,
      x: null,
      y: null,
      status: 'in_progress',
      lastOkMs: null,
      lastOkTaskId: null,
      lastOkRobot: null,
      trailVerified: null,
      trailInZoneUnits: null,
      trailDwellS: null,
      lastAttemptMs: null,
      lastAttemptState: null,
      lastAttemptTaskId: null,
      attempts: 0,
    });
  }
  return out;
}

/** The legend's far-end words: "3 d+ / never". */
export function staleLegendText(days: number): string {
  return `${days} d+ / never`;
}
